import { useMutation, useQueryClient } from "@tanstack/react-query";
import { completionsKey } from "../queries/completion-queries";
import { Completion } from "../types/Completion";
import { useStage } from "../queries/stage-queries";
import { useNavigate } from "react-router-dom";
import { useDeleteMissionMutation } from "./mission-mutator";
import { missionsKey, useMissions } from "../queries/mission-queries";
import { Mission } from "../types/Mission";
import { BackendAPI } from "../util/Env";
import { useMsal } from "@azure/msal-react";


export const useDeleteCompletionMutation = () => {
    const queryClient = useQueryClient();
    const { accounts } = useMsal();

    return useMutation({
        mutationFn: async (completionId: string) => {
            const response = await fetch(`${BackendAPI}/completions-api.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'ownerId': accounts[0].idTokenClaims!.oid!,
                },
                body: JSON.stringify({ id: completionId }),
            });

            if (!response.ok) {
                throw new Error(`Error deleting completion: ${response.statusText}`);
            }

            return response.json();
        },
        onMutate: async (completionId: string) => {
            await queryClient.cancelQueries({ queryKey: [completionsKey] });
            const prevCompletions = queryClient.getQueryData<Completion[]>([completionsKey]);

            queryClient.setQueryData([completionsKey], (old: Completion[] = []) =>
                old.filter((completion) => completion.id !== completionId)
            );

            return { prevCompletions };
        },
        onError: (_, _2, context) => {
            queryClient.setQueryData([completionsKey], context?.prevCompletions ?? []);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [completionsKey] });
        },
    });
};


export const useCreateCompletionMutation = () => {
    const { accounts } = useMsal();
    const { missingToNextStage } = useStage();
    useMissions();
    const deleteMissionMutation = useDeleteMissionMutation();

    const nav = useNavigate(); // To navigate user to the level-up page
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (newCompletion: Omit<Completion, 'id'>) => {
            const response = await fetch(`${BackendAPI}/completions-api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'ownerId': accounts[0].idTokenClaims!.oid!,
                },
                body: JSON.stringify(newCompletion),
            });

            if (!response.ok) {
                throw new Error(`Error creating completion: ${response.statusText}`);
            }

            return response.json();
        },
        onMutate: async (newCompletion: Omit<Completion, 'id'>) => {

            await queryClient.cancelQueries({ queryKey: [completionsKey] });

            const prevCompletions = queryClient.getQueryData<Completion[]>([completionsKey]);

            const tempCompletion = {
                ...newCompletion,
                id: `temp-${Date.now()}`,
            };
            queryClient.setQueryData([completionsKey], (old: Completion[] = []) => [
                ...old,
                tempCompletion,
            ]);

            const mission: Mission | undefined = (queryClient.getQueryData([missionsKey]) as Mission[] | undefined)?.find(
                (mission) => mission.id === newCompletion.missionId
            );

            if (mission && !mission.repeatable) {
                await deleteMissionMutation.mutateAsync(mission.id);
            }

            if (missingToNextStage <= newCompletion.coins) {
                sessionStorage.setItem('lvl-up', 'true');
                nav('/lvl-up', { state: 'lvl-up' });
            }

            return { prevCompletions };
        },
        onError: (_, _2, context) => {
            queryClient.setQueryData([completionsKey], context?.prevCompletions ?? []);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [completionsKey] });
        },
    });
};